import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { resourceFromAttributes } from '@opentelemetry/resources';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { BatchSpanProcessor } from '@opentelemetry/sdk-trace-web';
import { ZoneContextManager } from '@opentelemetry/context-zone';
import { trace, SpanStatusCode } from '@opentelemetry/api';


try {
  // Configure the OTLP trace exporter
  const exporter = new OTLPTraceExporter({
    url: 'https://apollo-new.bynry.com/v1/traces',
    headers: {
      // Add any required headers here if needed
    },
  });


  // Create the batch span processor
  const processor = new BatchSpanProcessor(exporter, {
    maxQueueSize: 2048,
    scheduledDelayMillis: 5000,
    exportTimeoutMillis: 30000,
    maxExportBatchSize: 512,
  });

  // Create a tracer provider
  const provider = new WebTracerProvider({
    resource: resourceFromAttributes({
      'service.name': 'smart360-frontend-dev',
      'service.version': '1.0.0',
      'service.environment': 'development',
    }),
    spanProcessors: [processor],
  });


  // Register the tracer provider with context manager
  provider.register({
    contextManager: new ZoneContextManager(),
  });


  // Setup React Error Tracking
  setupReactErrorTracking();
  
} catch (error) {
  console.error('Failed to initialize OpenTelemetry:', error);
  // Don't throw the error - let the app continue without telemetry
}

// Function to setup React error tracking only
function setupReactErrorTracking() {
  const tracer = trace.getTracer('react-error-tracking', '1.0.0');

  // Store original console methods
  const originalError = console.error;
  const originalWarn = console.warn;

  // Override console.error to capture React errors
  console.error = function(...args: any[]) {
    // Call original console.error first
    originalError.apply(console, args);
    
    // Check if this is a React error/warning
    const message = args.map(arg => String(arg)).join(' ');
    
    if (isReactError(message)) {
      const errorDetails = parseReactError(args);
      
      // Create an exception span for Signoz
      const span = tracer.startSpan('react.error', {
        kind: 1, // SpanKind.INTERNAL
        attributes: {
          'exception.type': 'react_error',
          'exception.message': errorDetails.message,
          'exception.stacktrace': errorDetails.stackTrace,
          'error.source': 'react',
          'error.level': 'error',
          'component.stack': errorDetails.componentStack,
        }
      });

      // Set span status as error for Signoz to recognize it as an exception
      span.setStatus({
        code: SpanStatusCode.ERROR,
        message: errorDetails.message
      });

      // Record as exception
      span.recordException({
        name: 'ReactError',
        message: errorDetails.message,
        stack: errorDetails.stackTrace,
      });

      span.end();
      
    }
  };

  // Override console.warn to capture React warnings
  console.warn = function(...args: any[]) {
    // Call original console.warn first
    originalWarn.apply(console, args);
    
    // Check if this is a React warning
    const message = args.map(arg => String(arg)).join(' ');
    
    if (isReactWarning(message)) {
      const warningDetails = parseReactError(args);
      
      // Create an exception span for Signoz (treat warnings as exceptions too)
      const span = tracer.startSpan('react.warning', {
        kind: 1, // SpanKind.INTERNAL
        attributes: {
          'exception.type': 'react_warning',
          'exception.message': warningDetails.message,
          'exception.stacktrace': warningDetails.stackTrace,
          'error.source': 'react',
          'error.level': 'warning',
          'component.stack': warningDetails.componentStack,
        }
      });

      // Set as error for Signoz to treat as exception
      span.setStatus({
        code: SpanStatusCode.ERROR,
        message: warningDetails.message
      });

      // Record as exception
      span.recordException({
        name: 'ReactWarning',
        message: warningDetails.message,
        stack: warningDetails.stackTrace,
      });

      span.end();
      
    }
  };

  // Global error handler for uncaught React errors
  window.addEventListener('error', (event) => {
    // Only capture if it's related to React
    if (event.filename && (event.filename.includes('.tsx') || event.filename.includes('.jsx') || 
        event.message.toLowerCase().includes('react'))) {
      
      const span = tracer.startSpan('react.uncaught.error', {
        kind: 1, // SpanKind.INTERNAL
        attributes: {
          'exception.type': 'react_uncaught_error',
          'exception.message': event.message,
          'exception.stacktrace': event.error?.stack || `${event.filename}:${event.lineno}:${event.colno}`,
          'error.source': 'react',
          'error.level': 'error',
          'error.filename': event.filename,
          'error.line': event.lineno,
          'error.column': event.colno,
        }
      });

      span.setStatus({
        code: SpanStatusCode.ERROR,
        message: event.message
      });

      span.recordException({
        name: 'ReactUncaughtError',
        message: event.message,
        stack: event.error?.stack || `${event.filename}:${event.lineno}:${event.colno}`,
      });

      span.end();
      
    }
  });

  // Global promise rejection handler for React-related promises
  window.addEventListener('unhandledrejection', (event) => {
    const reason = String(event.reason);
    
    // Only capture if it seems React-related
    if (reason.toLowerCase().includes('react') || 
        event.reason?.stack?.includes('.tsx') || 
        event.reason?.stack?.includes('.jsx')) {
      
      const span = tracer.startSpan('react.unhandled.promise.rejection', {
        kind: 1, // SpanKind.INTERNAL
        attributes: {
          'exception.type': 'react_promise_rejection',
          'exception.message': event.reason?.message || reason,
          'exception.stacktrace': event.reason?.stack || reason,
          'error.source': 'react',
          'error.level': 'error',
        }
      });

      span.setStatus({
        code: SpanStatusCode.ERROR,
        message: 'React Promise Rejection'
      });

      span.recordException({
        name: 'ReactPromiseRejection',
        message: event.reason?.message || reason,
        stack: event.reason?.stack || reason,
      });

      span.end();
      
    }
  });

}

// Check if the message is a React error
function isReactError(message: string): boolean {
  const reactErrorPatterns = [
    /^Error:/,
    /react/i,
    /component/i,
    /jsx/i,
    /tsx/i,
    /hook/i,
    /render/i,
    /virtual DOM/i,
    /hydration/i,
  ];
  
  return reactErrorPatterns.some(pattern => pattern.test(message));
}

// Check if the message is a React warning
function isReactWarning(message: string): boolean {
  const reactWarningPatterns = [
    /^Warning:/,
    /react/i,
    /component/i,
    /key/i,
    /prop/i,
    /hook/i,
    /render/i,
    /children/i,
    /validateDOMNesting/i,
    /findDOMNode/i,
    /componentWillReceiveProps/i,
    /componentWillMount/i,
    /componentWillUpdate/i,
  ];
  
  return reactWarningPatterns.some(pattern => pattern.test(message));
}

// Parse React error/warning to extract meaningful information
function parseReactError(args: any[]): { message: string; stackTrace: string; componentStack: string } {
  const fullMessage = args.map(arg => String(arg)).join(' ');
  
  // Split the message to separate the main message from stack trace
  const lines = fullMessage.split('\n');
  const messageLines: string[] = [];
  const stackLines: string[] = [];
  const componentStackLines: string[] = [];
  
  let inStackTrace = false;
  let inComponentStack = false;
  
  for (const line of lines) {
    const trimmedLine = line.trim();
    
    // Detect start of stack trace
    if (trimmedLine.startsWith('at ') || trimmedLine.includes('(http')) {
      inStackTrace = true;
    }
    
    // Detect React component stack
    if (trimmedLine.startsWith('at ') && (
        trimmedLine.includes('.tsx:') || 
        trimmedLine.includes('.jsx:') || 
        /at [A-Z]/.test(trimmedLine) // React components start with capital letter
    )) {
      inComponentStack = true;
    }
    
    if (inStackTrace) {
      if (inComponentStack) {
        componentStackLines.push(trimmedLine);
      } else {
        stackLines.push(trimmedLine);
      }
    } else {
      messageLines.push(line);
    }
  }
  
  const message = messageLines.join('\n').trim();
  const stackTrace = [...stackLines, ...componentStackLines].join('\n');
  const componentStack = componentStackLines.join('\n');
  
  return {
    message: message || fullMessage,
    stackTrace: stackTrace || 'No stack trace available',
    componentStack: componentStack || 'No component stack available'
  };
}

export default {}